import { useState } from 'react';
import { Nav, Badge, Button, Dropdown, Modal, Form, Alert } from 'react-bootstrap';
import { Plus, Search, Trash2, Edit, ExternalLink, MoreVertical } from 'lucide-react';
import type { ProductData, CompetitorProduct } from '../models/types';
import InsertCompetitorProductModal from './InsertCompetitorProductModal';
import type { CompetitorClient } from '../../../../infrastructure/CompetitorClient';
import { useInsertCompetitorProduct } from '../../../../apps/hooks/competitor/useInsertCompetitorProduct';
import type { Competitor } from '../../../../models/ProductTypes';

interface CompetitorSelectionSectionProps {
  readonly selectedProduct: ProductData;
  readonly activeCompetitor: string | null;
  readonly selectedProducts: Record<string, Record<string, string>>;
  readonly handleCompetitorProductSelect: (product?: CompetitorProduct) => void;
  readonly isProductSelected: (skinSeoulSku: string, competitor: string, competitorSku: string) => boolean;
  readonly setActiveCompetitor: (competitor: string | null) => void;
  readonly competitorClient: CompetitorClient;
  readonly onProductDataUpdate: (updatedProduct: ProductData) => void;
}

const competitorSearchUrls: Record<string, string> = {
  YesStyle: 'https://www.yesstyle.com/en/list.html?q=',
  Stylevana: 'https://www.stylevana.com/en_US/catalogsearch/result/?q=',
  StyleKorean: 'https://www.stylekorean.com/shop/search_result.php?keyword=',
  OliveYoung: 'https://global.oliveyoung.com/display/search?query=',
  Amazon: 'https://www.amazon.com/s?k=',
};

export default function CompetitorSelectionSection({
  selectedProduct,
  activeCompetitor,
  selectedProducts,
  handleCompetitorProductSelect,
  isProductSelected,
  setActiveCompetitor,
  competitorClient,
  onProductDataUpdate,
}: CompetitorSelectionSectionProps) {
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showUnmatchModal, setShowUnmatchModal] = useState(false);
  const [selectedCompetitorProduct, setSelectedCompetitorProduct] = useState<CompetitorProduct | null>(null);
  const [newUrl, setNewUrl] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const { handleInsert } = useInsertCompetitorProduct(
    competitorClient,
    selectedProduct,
    onProductDataUpdate,
    setActiveCompetitor,
  );

  const skinSeoulProduct = selectedProduct.skinSeoulProduct;

  const handleCompetitorSearch = (competitor: string) => {
    if (!skinSeoulProduct?.name) return;
    const searchTerm = encodeURIComponent(skinSeoulProduct.name);
    window.open(`${competitorSearchUrls[competitor]}${searchTerm}`, '_blank');
  };

  const getCompetitorProducts = (): CompetitorProduct[] => {
    if (!activeCompetitor) return [];
    const machineProducts =
      selectedProduct.machineMatchedProducts.find((c) => c.competitor === activeCompetitor)?.competitorProducts ?? [];
    const humanProducts =
      selectedProduct.humanMatchedProducts?.find((c) => c.competitor === activeCompetitor)?.competitorProducts ?? [];

    const humanSkus = new Set(humanProducts.map((p) => p.sku));
    const mergedProducts: CompetitorProduct[] = [
      ...humanProducts.map((p) => ({ ...p, matchType: 'human' as const })),
      ...machineProducts.filter((p) => !humanSkus.has(p.sku)).map((p) => ({ ...p, matchType: 'machine' as const })),
    ];
    return mergedProducts;
  };

  const getAllCompetitors = () => {
    const competitorSet = new Map<string, number>();
    selectedProduct.machineMatchedProducts.forEach((c) => {
      competitorSet.set(c.competitor, (competitorSet.get(c.competitor) || 0) + c.competitorProducts.length);
    });
    selectedProduct.humanMatchedProducts?.forEach((c) => {
      competitorSet.set(c.competitor, (competitorSet.get(c.competitor) || 0) + c.competitorProducts.length);
    });
    return Array.from(competitorSet.entries())
      .filter(([, count]) => count > 0)
      .map(([competitor]) => ({ competitor }));
  };

  const competitorsWithProducts = getAllCompetitors();
  const competitorProducts = getCompetitorProducts();

  const getFirstImageUrl = (urls: any): string => {
    if (!urls) return '';
    if (Array.isArray(urls)) return urls[0]?.trim() || '';
    if (typeof urls === 'string') return urls.split(',')[0]?.trim() || '';
    return '';
  };

  const handleUnmatch = async () => {
    if (!selectedCompetitorProduct || !activeCompetitor) return;
    setIsActionLoading(true);
    setActionError(null);
    try {
      await competitorClient.unmatchCompetitor({
        skinSeoulSku: skinSeoulProduct.sku,
        competitor: activeCompetitor as Competitor,
        competitorSku: selectedCompetitorProduct.sku,
      });
      setShowUnmatchModal(false);
      setSelectedCompetitorProduct(null);
    } catch (err) {
      setActionError('Failed to unmatch competitor product.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleReplaceUrl = async () => {
    if (!selectedCompetitorProduct || !activeCompetitor || !newUrl) return;
    setIsActionLoading(true);
    setActionError(null);
    try {
      await competitorClient.replaceCompetitorUrl({
        skinSeoulSku: skinSeoulProduct.sku,
        competitor: activeCompetitor as Competitor,
        oldCompetitorSku: selectedCompetitorProduct.sku,
        newProductLink: newUrl,
      });
      setShowReplaceModal(false);
      setSelectedCompetitorProduct(null);
      setNewUrl('');
    } catch (err) {
      setActionError('Failed to replace competitor URL.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const openActionModal = (product: CompetitorProduct, action: 'unmatch' | 'replace') => {
    setSelectedCompetitorProduct(product);
    setActionError(null);
    if (action === 'unmatch') setShowUnmatchModal(true);
    else if (action === 'replace') {
      setNewUrl(product.productLink);
      setShowReplaceModal(true);
    }
  };

  return (
    <div className="bg-white rounded-3 p-3 d-flex flex-column h-100" style={{ overflow: 'hidden' }}>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="fw-bold" style={{ fontSize: '1rem' }}>
          Competitor Products
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              <Search size={14} className="me-1" />
              Search
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.keys(competitorSearchUrls).map((c) => (
                <Dropdown.Item key={c} onClick={() => handleCompetitorSearch(c)}>
                  {c}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Button variant="outline-primary" size="sm" onClick={() => setShowInsertModal(true)}>
            <Plus size={14} className="me-1" />
            Insert
          </Button>
        </div>
      </div>

      {competitorsWithProducts.length > 0 ? (
        <>
          <Nav
            variant="tabs"
            activeKey={activeCompetitor ?? ''}
            onSelect={(key) => setActiveCompetitor(key)}
            className="mb-3 border-bottom-0"
          >
            {competitorsWithProducts.map((c) => {
              const isMatched = selectedProducts?.[skinSeoulProduct.sku]?.[c.competitor] !== undefined;
              return (
                <Nav.Item key={c.competitor}>
                  <Nav.Link eventKey={c.competitor} className={isMatched ? 'text-success' : ''}>
                    {c.competitor}
                    {isMatched && (
                      <Badge bg="success" className="ms-1">
                        ✓
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
              );
            })}
          </Nav>

          <div style={{ overflowY: 'auto', flexGrow: 1 }}>
            {activeCompetitor && competitorProducts.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {competitorProducts.map((product, index) => {
                  const isSelected = isProductSelected(skinSeoulProduct.sku, activeCompetitor, product.sku);
                  const matchType = product.matchType || 'machine';

                  return (
                    <div
                      key={product.sku}
                      className={`p-3 border rounded ${isSelected ? 'border-success border-2 bg-success bg-opacity-10' : 'bg-white'}`}
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                      onClick={() => handleCompetitorProductSelect(product)}
                    >
                      <div className="d-flex gap-3">
                        <div className="position-relative" style={{ minWidth: '80px' }}>
                          {product.productImageUrls ? (
                            <img
                              src={getFirstImageUrl(product.productImageUrls)}
                              alt={product.normalizedName}
                              style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '6px' }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center bg-light text-muted"
                              style={{ width: '80px', height: '80px', borderRadius: '6px', fontSize: '0.7rem' }}
                            >
                              No image
                            </div>
                          )}
                          <Badge
                            bg="dark"
                            className="position-absolute"
                            style={{
                              top: '-6px',
                              left: '-6px',
                              borderRadius: '50%',
                              width: '22px',
                              height: '22px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {index + 1}
                          </Badge>
                          {isSelected && (
                            <Badge
                              bg="success"
                              className="position-absolute"
                              style={{
                                bottom: '-6px',
                                right: '-6px',
                                borderRadius: '50%',
                                width: '22px',
                                height: '22px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              ✓
                            </Badge>
                          )}
                          {matchType === 'human' && (
                            <Badge
                              bg="success"
                              className="position-absolute"
                              style={{
                                top: '-6px',
                                right: '-6px',
                                borderRadius: '4px',
                                width: '22px',
                                height: '22px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                              }}
                              title="Human Verified"
                            >
                              ✓
                            </Badge>
                          )}
                        </div>

                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="mb-0" style={{ fontSize: '0.9rem' }}>
                              {product.normalizedName}
                            </h6>
                            {product.possibility && (
                              <Badge
                                bg={product.possibility > 0.8 ? 'success' : product.possibility > 0.6 ? 'warning' : 'danger'}
                                style={{ fontSize: '0.65rem' }}
                              >
                                {Math.round(product.possibility * 100)}%
                              </Badge>
                            )}
                          </div>

                          <div className="small text-muted mb-1">
                            SKU: {product.sku} | Brand: {product.brand}
                          </div>
                          <div className="small text-muted mb-2">
                            Size: {product.size || 'N/A'} | Pack: {product.pack || 'N/A'}
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                              {(() => {
                                const directPrice =
                                  product.price?.[0]?.salesPrice ??
                                  product.price?.[0]?.originalPrice ??
                                  product.originalPrice ??
                                  product.salePrice;
                                const ratio =
                                  product.normalizationRatio ?? product.normalization?.computedRatio;
                                const normalizedPrice =
                                  product.normalizedPrice ?? product.normalization?.normalizedPrice;
                                const originalPrice =
                                  directPrice ?? (ratio && normalizedPrice ? normalizedPrice / ratio : undefined);
                                if (originalPrice == null) return null;
                                return (
                                  <>
                                    <span className="fw-bold text-primary">${originalPrice.toFixed(2)}</span>
                                    {normalizedPrice != null && ratio != null && (
                                      <span className="small text-muted">
                                        (×{ratio.toFixed(2)} →{' '}
                                        <span className="text-success fw-bold">${normalizedPrice.toFixed(2)}</span>)
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>

                            <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                href={product.productLink}
                                target="_blank"
                                as="a"
                                title={`View on ${activeCompetitor}`}
                              >
                                <ExternalLink size={12} />
                              </Button>
                              <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                  <MoreVertical size={12} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => openActionModal(product, 'replace')}>
                                    <Edit size={14} className="me-2" />
                                    Replace URL
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item className="text-danger" onClick={() => openActionModal(product, 'unmatch')}>
                                    <Trash2 size={14} className="me-2" />
                                    Unmatch
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                {activeCompetitor ? 'No products for this competitor' : 'Select a competitor tab'}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center text-muted py-4">No competitor products found</div>
      )}

      <InsertCompetitorProductModal show={showInsertModal} onHide={() => setShowInsertModal(false)} onSubmit={handleInsert} />

      {selectedCompetitorProduct && (
        <>
          <Modal show={showUnmatchModal} onHide={() => setShowUnmatchModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Unmatch</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {actionError && <Alert variant="danger">{actionError}</Alert>}
              <p>
                Are you sure you want to unmatch <strong>{selectedCompetitorProduct.normalizedName}</strong> from{' '}
                <strong>{skinSeoulProduct.name}</strong>?
              </p>
              <p className="text-muted small">This competitor product will no longer affect pricing calculations.</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowUnmatchModal(false)} disabled={isActionLoading}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleUnmatch} disabled={isActionLoading}>
                {isActionLoading ? 'Processing...' : 'Unmatch'}
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showReplaceModal} onHide={() => setShowReplaceModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Replace Competitor URL</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {actionError && <Alert variant="danger">{actionError}</Alert>}
              <p>
                Replace URL for <strong>{selectedCompetitorProduct.normalizedName}</strong>
              </p>
              <Form.Group>
                <Form.Label>New Product URL</Form.Label>
                <Form.Control
                  type="url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://..."
                />
              </Form.Group>
              <p className="text-muted small mt-2">This will update the match to Human Match status.</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowReplaceModal(false)} disabled={isActionLoading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleReplaceUrl} disabled={isActionLoading || !newUrl}>
                {isActionLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
}
